import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { companiesApi } from '../../api/companies';
import { useCompany } from '../../contexts/CompanyContext';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { Plus, Trash2, X, Users } from 'lucide-react';
import type { CompanyMemberRole } from '../../types';
import EmptyState from '../EmptyState';

const ROLES: { value: CompanyMemberRole; label: string; description: string }[] = [
  { value: 'admin', label: 'Admin', description: 'Full access to settings and all tickets' },
  { value: 'technician', label: 'Technician', description: 'Can manage assigned tickets and time entries' },
  { value: 'customer', label: 'Customer', description: 'Can create tickets and view own tickets' },
];

export default function MembersTab() {
  const { currentCompany } = useCompany();
  const { user } = useAuth();
  const companyId = (currentCompany as any)?.id ?? (currentCompany as any)?.uuid;
  const queryClient = useQueryClient();
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<CompanyMemberRole>('technician');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const { data: membersData = [] } = useQuery({
    queryKey: ['company-members', companyId],
    queryFn: () => companiesApi.getMembers(companyId),
    enabled: !!companyId,
  });

  const members = membersData as any[];

  const addMemberMutation = useMutation({
    mutationFn: ({ email, role }: { email: string; role: CompanyMemberRole }) =>
      companiesApi.addMember(companyId, email, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-members', companyId] });
      toast.success('Member added');
      setShowInvite(false);
      setInviteEmail('');
      setInviteRole('technician');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: CompanyMemberRole }) =>
      companiesApi.updateMember(companyId, userId, { role }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-members', companyId] });
      toast.success('Role updated');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // "Update text in emails by default": tickets this person files, or is the
  // customer on, start with the actual reply text in their emails instead of
  // a bare link. Turning it on also covers their currently open tickets.
  const updateContentDefaultMutation = useMutation({
    mutationFn: ({ userId, on }: { userId: string; on: boolean; name: string }) =>
      companiesApi.updateMember(companyId, userId, { contentEmailsDefault: on }),
    onSuccess: (res, { on, name }) => {
      queryClient.invalidateQueries({ queryKey: ['company-members', companyId] });
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['ticket'] });
      const applied = res.data?.appliedToTickets ?? 0;
      toast.success(on
        ? `Update text in emails is on for ${name}${applied > 0 ? `. Also applied to ${applied} open ticket${applied === 1 ? '' : 's'}.` : '.'}`
        : `Update text in emails is off for ${name}. Existing tickets keep their current setting.`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const removeMemberMutation = useMutation({
    mutationFn: (userId: string) => companiesApi.removeMember(companyId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company-members', companyId] });
      toast.success('Member removed');
      setDeleteConfirm(null);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    if (!inviteEmail.trim()) return toast.error('Email is required');
    addMemberMutation.mutate({ email: inviteEmail.trim(), role: inviteRole });
  }

  const currentUserId = user?.id;

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="section-title">Members</h2>
        {!showInvite && (
          <button
            className="btn btn-primary inline-flex items-center gap-2"
            onClick={() => setShowInvite(true)}
          >
            <Plus className="w-4 h-4" />
            Add Member
          </button>
        )}
      </div>

      {showInvite && (
        <form onSubmit={handleInvite} className="mb-6 p-4 border border-line rounded-lg space-y-4 bg-row-odd">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-fg">Add Member</h3>
            <button type="button" onClick={() => setShowInvite(false)} className="text-muted hover:text-fg">
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-sm text-muted">The user must already have an account. Enter their email address.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="label">Email Address</label>
              <input
                type="email"
                className="input"
                value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
                placeholder="user@example.com"
              />
            </div>
            <div>
              <label className="label">Role</label>
              <select
                className="select w-full"
                value={inviteRole}
                onChange={e => setInviteRole(e.target.value as CompanyMemberRole)}
              >
                {ROLES.map(r => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <button type="submit" className="btn btn-primary" disabled={addMemberMutation.isPending}>
              {addMemberMutation.isPending ? 'Adding...' : 'Add Member'}
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => setShowInvite(false)}>Cancel</button>
          </div>
        </form>
      )}

      {members.length === 0 ? (
        <EmptyState icon={<Users className="w-6 h-6" />}>
          No members yet. Click <strong>Add Member</strong> to invite an existing user to this company.
        </EmptyState>
      ) : (
        <div className="space-y-1">
          <p className="text-[11px] text-muted leading-relaxed pb-2 border-b border-line-light">
            <span className="font-semibold text-fg">Update text in emails:</span> tickets this person files, or is the
            customer on (contact, reporter email, or CC), start with the actual reply and note text in notification emails
            instead of just a link. Turning it on also applies to their open tickets. Only for people whose tickets never
            contain PHI or other sensitive data. Each ticket and each update can still be switched individually.
          </p>
          {members.map((m: any) => {
            const userId = m.user?.id ?? m.userId;
            const isOwner = m.role === 'owner';
            const isSelf = userId === currentUserId;
            const displayName = m.user?.name || m.user?.email || 'this member';

            return (
              <div key={userId} className="flex items-center justify-between py-3 border-b border-line-light last:border-0">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-primary-muted flex items-center justify-center text-primary font-semibold text-sm shrink-0">
                    {(m.user?.name || m.user?.email || '?')[0].toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-fg truncate">
                        {m.user?.name ?? 'Unknown'}
                      </span>
                      {isSelf && <span className="text-xs text-muted">(you)</span>}
                    </div>
                    <span className="text-sm text-muted truncate block">{m.user?.email}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3 ml-4 shrink-0">
                  <label
                    className="flex items-center gap-1.5 text-xs cursor-pointer select-none"
                    style={{ color: m.contentEmailsDefault ? 'var(--scpa-status-warning)' : 'var(--scpa-text-muted)' }}
                    title={m.contentEmailsDefault
                      ? `${displayName}'s tickets send the actual update text in emails by default`
                      : `Send the actual update text (not just a link) in emails for tickets ${displayName} files or is the customer on`}
                  >
                    <input
                      type="checkbox"
                      className="rounded"
                      checked={!!m.contentEmailsDefault}
                      onChange={e => updateContentDefaultMutation.mutate({ userId, on: e.target.checked, name: displayName })}
                      disabled={updateContentDefaultMutation.isPending}
                    />
                    Update text in emails
                  </label>
                  {isOwner ? (
                    <span className="badge text-warn">Owner</span>
                  ) : (
                    <select
                      className="select text-sm py-1"
                      value={m.role}
                      onChange={e => updateRoleMutation.mutate({ userId, role: e.target.value as CompanyMemberRole })}
                      disabled={isSelf || updateRoleMutation.isPending}
                    >
                      {ROLES.map(r => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  )}
                  {!isOwner && !isSelf && (
                    deleteConfirm === userId ? (
                      <div className="flex items-center gap-1">
                        <button
                          className="text-xs btn btn-danger py-1 px-2"
                          onClick={() => removeMemberMutation.mutate(userId)}
                          disabled={removeMemberMutation.isPending}
                        >
                          Remove
                        </button>
                        <button className="text-xs btn btn-ghost py-1 px-2" onClick={() => setDeleteConfirm(null)}>
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        className="p-1.5 text-muted hover:text-err rounded transition-colors"
                        onClick={() => setDeleteConfirm(userId)}
                        title="Remove member"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
